import React, { useState, useEffect, useCallback } from 'react';
import { getCurrentSession } from '../services/authService';
import { createDispatcher, removeDispatcher } from '../services/adminApi';
import { Card, Table, Drawer, ConfirmDialog, Field, TextInput, Select, Button, StatusBadge, Alert } from '../ui/primitives';
import { isValidPhone, isValidEmail } from '../charter/validation';

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';

// `role` is an ALLOW-LISTED token the server maps to a configured Cognito group — never a group name.
// AdminProvisioningService refuses anything outside {dispatcher, admin}, so this control cannot widen
// access beyond the two staff roles a company administrator is permitted to create.
const ROLE_DISPATCHER = 'dispatcher';
const ROLE_ADMIN = 'admin';
const EMPTY_FORM = { firstName: '', lastName: '', email: '', phoneNumber: '', role: ROLE_DISPATCHER };

async function getAuthHeader(authSession) {
  let token = authSession?.idToken;
  try { const s = await getCurrentSession(); token = s.idToken; } catch (e) {}
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

function Kpi({ label, value, sub }) {
  return (
    <Card className="px-4 py-3">
      <div className="text-11 font-semibold uppercase tracking-wider text-body/50">{label}</div>
      <div className="mt-1.5 text-[30px] leading-none font-bold text-primary font-mono tabular-nums tracking-tight">{value}</div>
      {sub && <div className="mt-1.5 text-[12px] font-medium text-body/55">{sub}</div>}
    </Card>
  );
}

// Admin-only roster of dispatchers. "Add" provisions a real Cognito login (invite email sent)
// via /api/admin/dispatchers; "Remove" disables the login and deactivates the row.
function OpsDispatchers({ authSession }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const fetchData = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeader(authSession);
      const empRes = await fetch(`${API_BASE}/api/company-employees`, { headers });
      if (empRes.ok) setEmployees(await empRes.json());
    } catch (err) {
      setError('Failed to load dispatchers');
    } finally {
      setLoading(false);
    }
  }, [authSession]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Dispatchers AND administrators are provisioned here, so both belong on this roster — otherwise an
  // admin created through this form would vanish from the list that created them. Inactive rows are
  // kept visible so a removed person's history stays legible.
  const dispatchers = employees.filter((e) => {
    const r = (e.role || '').toLowerCase();
    return r === 'dispatcher' || r === 'admin';
  });

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
    setFormError(null);
  };

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setFormError('First and last name are required');
      return;
    }
    if (!form.email.trim()) {
      setFormError('Email is required — the Cognito invite is sent there');
      return;
    }
    if (!isValidEmail(form.email)) {
      setFormError("That email doesn't look right");
      return;
    }
    if (form.phoneNumber.trim() && !isValidPhone(form.phoneNumber)) {
      setFormError('Enter a 10-digit phone number with the area code');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await createDispatcher({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phoneNumber: form.phoneNumber.trim() || null,
        role: form.role === ROLE_ADMIN ? ROLE_ADMIN : ROLE_DISPATCHER,
      }, authSession);
      setDrawerOpen(false);
      await fetchData({ silent: true });
    } catch (e) {
      setFormError(e.message || 'Failed to create dispatcher');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (emp) => {
    if (!emp) return;
    setRemovingId(emp.employeeId);
    try {
      if (emp.userId) {
        // Has a Cognito login → delete the login + the DB row.
        await removeDispatcher(emp.userId, authSession);
      } else {
        // Seeded / login-less row (no Cognito user): just delete the DB row.
        const headers = await getAuthHeader(authSession);
        const res = await fetch(`${API_BASE}/api/company-employees/${emp.employeeId}`, { method: 'DELETE', headers });
        if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
      }
      await fetchData({ silent: true });
    } catch (e) {
      setActionError(`Failed to remove dispatcher: ${e.message}`);
    } finally {
      setRemovingId(null);
    }
  };

  const activeCount = dispatchers.filter(e => e.isActive).length;

  const columns = [
    { key: 'id', header: 'ID', mono: true, render: (e) => `#${e.employeeId}` },
    { key: 'name', header: 'Name', render: (e) => `${e.firstName} ${e.lastName}` },
    { key: 'email', header: 'Email', render: (e) => e.email || '-' },
    { key: 'phone', header: 'Phone', render: (e) => e.phoneNumber || '-' },
    { key: 'status', header: 'Status', render: (e) => <StatusBadge status={e.isActive ? 'active' : 'inactive'} /> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (e) => (
        <Button
          variant="danger"
          size="sm"
          disabled={removingId === e.employeeId}
          onClick={() => setConfirmTarget(e)}
        >
          {removingId === e.employeeId ? '...' : 'Remove'}
        </Button>
      ),
    },
  ];

  if (loading) {
    return <div className="h-full grid place-items-center bg-surface text-body/55 text-13">Loading dispatchers…</div>;
  }
  if (error) {
    return (
      <div className="h-full grid place-items-center bg-surface p-6">
        <Alert tone="danger" title="Couldn't load dispatchers" className="max-w-md">
          {error}
          <div className="mt-2"><Button variant="outline" size="sm" onClick={fetchData}>Retry</Button></div>
        </Alert>
      </div>
    );
  }

  const confirmLabel = confirmTarget
    ? (`${confirmTarget.firstName} ${confirmTarget.lastName}`.trim() || `dispatcher #${confirmTarget.employeeId}`)
    : '';

  return (
    <div className="h-full min-h-0 flex flex-col gap-4 p-5 bg-surface">
      {actionError && <Alert key={actionError} tone="danger" dismissible className="shrink-0">{actionError}</Alert>}

      <header className="shrink-0 flex items-center gap-3">
        <div className="min-w-0">
          <h1 className="text-15 font-bold text-primary leading-tight">Dispatchers</h1>
          <div className="text-[12px] text-body/55 leading-tight">Add or remove dispatcher logins</div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Button variant="primary" onClick={openAdd}>+ Add dispatcher</Button>
        </div>
      </header>

      <div className="shrink-0 grid grid-cols-3 gap-3">
        <Kpi label="Dispatchers" value={dispatchers.length} sub="on roster" />
        <Kpi label="Active" value={activeCount} sub="can sign in" />
        <Kpi label="Inactive" value={dispatchers.length - activeCount} sub="removed" />
      </div>

      <Table className="flex-1 min-h-0" columns={columns} rows={dispatchers.map(e => ({ ...e, id: e.employeeId }))} empty="No dispatchers yet" />

      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        title="Add dispatcher"
        footer={
          <>
            <Button variant="outline" onClick={closeDrawer} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Creating…' : 'Create login'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {formError && <Alert tone="danger">{formError}</Alert>}
          <div className="text-[12px] text-body/55">
            A Cognito login is created and an invite email with a temporary password is sent to the address below.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="First name *">
              <TextInput value={form.firstName} onChange={e => updateField('firstName', e.target.value)} />
            </Field>
            <Field label="Last name *">
              <TextInput value={form.lastName} onChange={e => updateField('lastName', e.target.value)} />
            </Field>
          </div>

          <Field label="Email *" hint="the invite is sent here">
            <TextInput type="email" value={form.email} onChange={e => updateField('email', e.target.value)} placeholder="dispatcher@example.com" />
          </Field>

          <Field
            label="Role *"
            hint={form.role === ROLE_ADMIN
              ? 'Full access: the operations dashboard, plus inviting drivers and dispatchers.'
              : 'Access to the dispatch board only.'}
          >
            <Select value={form.role} onChange={e => updateField('role', e.target.value)}>
              <option value={ROLE_DISPATCHER}>Dispatcher — dispatch board</option>
              <option value={ROLE_ADMIN}>Administrator — full operations access</option>
            </Select>
          </Field>

          <Field label="Phone">
            <TextInput value={form.phoneNumber} onChange={e => updateField('phoneNumber', e.target.value)} placeholder="+1 555 123 4567" />
          </Field>
        </div>
      </Drawer>

      <ConfirmDialog
        open={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        onConfirm={() => handleRemove(confirmTarget)}
        title="Remove dispatcher"
        message={confirmTarget ? `Remove ${confirmLabel}? Their login will be disabled.` : ''}
        confirmLabel="Remove"
        variant="danger"
      />
    </div>
  );
}

export default OpsDispatchers;
